import React from 'react';
import { ArrowRight, GraduationCap, Users, FileText, TrendingUp, Signpost, Calendar, Info, Megaphone, Globe, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { isApplicationsClosed, APPLICATION_URL } from '../lib/summerSchoolStatus';

const SummerSchoolPage = () => {
  const closed = isApplicationsClosed();
  return (
    <div className="min-h-screen bg-white" data-testid="summer-school-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-emerald-700 text-white py-20">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <p className="uppercase tracking-widest text-emerald-200 text-sm font-semibold mb-3">
              School of Advanced Studies
            </p>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Applications Open for the 2026 SAS Summer School
            </h1>
            <p className="text-xl md:text-2xl text-emerald-50 mb-8">
              An intensive research training programme at The Highland Institute
            </p>
            <div className="flex flex-wrap gap-4">
              {closed ? (
                <div
                  className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-semibold px-6 py-4 rounded-md"
                  data-testid="summer-school-closed-badge-hero"
                >
                  <Lock className="w-5 h-5" />
                  Applications Closed
                </div>
              ) : (
                <a href={APPLICATION_URL} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="bg-white text-green-900 hover:bg-emerald-50 font-semibold px-8 py-6 text-lg"
                    data-testid="summer-school-apply-btn-hero"
                  >
                    Apply Now <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              )}
              <a href="#details">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-green-900 font-semibold px-8 py-6 text-lg"
                  data-testid="summer-school-details-btn"
                >
                  View Programme Details
                </Button>
              </a>
            </div>
            <p className="mt-6 text-emerald-100 text-sm">
              {closed ? (
                <>
                  Applications closed on <span className="font-bold text-white">27 May 2026</span>.
                  Thank you to all who applied.
                </>
              ) : (
                <>
                  Application deadline: <span className="font-bold text-white">27 May 2026</span>
                </>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Poster Section */}
      <section className="py-16 bg-gray-50" id="details">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
              <img
                src="/summer-school-2026-poster.png"
                alt="The Highland Institute — Applications Open for the 2026 SAS Summer School"
                className="w-full h-auto"
                data-testid="summer-school-poster"
              />
            </div>
            <div className="mt-6 text-center">
              <a href="/summer-school-2026-poster.png" target="_blank" rel="noopener noreferrer">
                <Button
                  variant="outline"
                  className="border-green-700 text-green-800 hover:bg-green-50"
                  data-testid="summer-school-poster-download-btn"
                >
                  Download Poster
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* A pathway into advanced research */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <GraduationCap className="w-7 h-7 text-green-800" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-900 mb-3">A pathway into advanced research</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The School of Advanced Studies (SAS) Summer School is an intensive training programme for
                  final-year master's students and recent graduates preparing for advanced academic and research
                  pathways. It serves as the initial intensive phase of the Highland Institute's 12-month
                  Postgraduate Certificate in Research (PGCertR), while also functioning as a standalone advanced
                  training programme.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two-column details */}
      <section className="py-12 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Who should apply */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-700 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-900">Who should apply?</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Final-year master's students and recent master's graduates who are preparing for further research,
                considering doctoral study, or seeking advanced training in research methods and academic writing.
              </p>
            </div>

            {/* The PGCertR pathway */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-700 flex items-center justify-center">
                  <Signpost className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-900">The PGCertR pathway</h3>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3">
                The Summer School is the entry phase of the Highland Institute's 12-month Postgraduate Certificate
                in Research (PGCertR). The PGCertR is designed to support:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>article development and publication</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>doctoral application mentorship</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>advanced research training</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>preparation for future research opportunities within the Highland Institute</span>
                </li>
              </ul>
            </div>

            {/* What does the programme offer */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-700 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-900">What does the programme offer?</h3>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Advanced Research Methodology</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Research Ethics</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Applied Field Research</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Academic Writing</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Basic digital skills</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-700 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Soft skills</span>
                </li>
              </ul>
            </div>

            {/* Duration */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-700 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-900">Duration</h3>
              </div>
              <p className="text-xl font-semibold text-gray-900 mb-2">15 June — 31 August 2026</p>
              <p className="text-gray-700 leading-relaxed">
                A two-and-a-half-month intensive programme at The Highland Institute.
              </p>
            </div>

            {/* How will you benefit */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-700 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-900">How will you benefit?</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                The programme develops research skills, fieldwork experience, and interdisciplinary insights for
                advanced academic, professional, and research trajectories.
              </p>
            </div>

            {/* Important information */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-700 flex items-center justify-center">
                  <Info className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-900">Important information</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                The programme is <strong>free of charge</strong>. The Summer School is an on-site programme held
                three days per week. Travel and accommodation are not supported by the Institute. Logistical
                support will be provided.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Apply CTA / Closed Notice */}
      <section className="py-20 bg-gradient-to-r from-green-800 to-emerald-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {closed ? (
              <>
                <Lock className="w-12 h-12 mx-auto mb-4 text-emerald-200" />
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Applications Closed
                </h2>
                <p className="text-xl text-emerald-50 mb-2">
                  The application window for the 2026 SAS Summer School closed on 27 May 2026.
                </p>
                <p className="text-emerald-100">
                  For future programmes and enquiries, please contact{' '}
                  <a
                    href="mailto:lanu@highlandinstitute.org"
                    className="underline hover:text-white"
                  >
                    lanu@highlandinstitute.org
                  </a>
                  .
                </p>
              </>
            ) : (
              <>
                <Megaphone className="w-12 h-12 mx-auto mb-4 text-emerald-200" />
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Application deadline: 27 May 2026
                </h2>
                <p className="text-xl text-emerald-50 mb-8">
                  Secure your place in the 2026 SAS Summer School cohort.
                </p>
                <a href={APPLICATION_URL} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="bg-white text-green-900 hover:bg-emerald-50 font-semibold px-10 py-6 text-lg"
                    data-testid="summer-school-apply-btn-footer"
                  >
                    Apply Now <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Globe className="w-6 h-6 text-green-800" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">For full details and applications</h4>
                {closed ? (
                  <span className="text-gray-500 italic" data-testid="summer-school-apply-closed-text">
                    Applications closed on 27 May 2026
                  </span>
                ) : (
                  <a
                    href={APPLICATION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-700 hover:text-green-900 underline break-all"
                    data-testid="summer-school-apply-link"
                  >
                    Apply via Google Forms
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-800" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">Enquiries</h4>
                <p className="text-gray-700">Lanuakum Aier</p>
                <a
                  href="mailto:lanu@highlandinstitute.org"
                  className="text-green-700 hover:text-green-900 underline"
                  data-testid="summer-school-email-link"
                >
                  lanu@highlandinstitute.org
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SummerSchoolPage;
